import { Mesh, type Object3D } from 'three'
import MeshFileLoader from './mesh-file-loader'
import { ThreeOriginalFileTypes } from '../../../constants/three'
import { STLLoader } from 'three/examples/jsm/Addons.js'
import { setFileLoadingStatus } from '../../../stores/file-loading'
import { splitConnectedComponents, toIndexedGeometry } from '../../../helpers/three/mesh-indexing'

class STLFileLoader extends MeshFileLoader {
  protected getWorkerFileType(): FileParserSupportedType {
    return 'stl'
  }

  protected getOriginalFileType(): typeof ThreeOriginalFileTypes[keyof typeof ThreeOriginalFileTypes] {
    return ThreeOriginalFileTypes.STL
  }

  protected processParsedObjects(parsedObjects: Object3D[]): Object3D[] {
    const meshes: Mesh[] = []

    for (const obj of parsedObjects) {
      obj.traverse((child) => {
        if (child instanceof Mesh) {
          const scaled = this.scaleDownObject(child) as Mesh
          scaled.material = this.getStandardMaterial()
          meshes.push(scaled)
        }
      })
    }

    return meshes
  }

  protected processOnMainThread(): Mesh[] {
    setFileLoadingStatus('parsing', this.file.name, 20)

    const loader = new STLLoader()
    // Index the triangle soup up front (STL is non-indexed): lighter geometry everywhere
    // downstream, and connected-component splitting can rely on the shared index.
    const bufferGeometry = toIndexedGeometry(loader.parse(this.arrayBuffer!))

    this.arrayBuffer = undefined

    setFileLoadingStatus('processing', this.file.name, 40)

    const strategy = this.getOptimizationStrategy()
    const geometries = strategy.shouldSplit
      ? splitConnectedComponents(bufferGeometry)
      : [bufferGeometry]

    setFileLoadingStatus('finalizing', this.file.name, 80)

    // Geometry is no longer simplified anywhere — meshes are kept at full resolution.
    return geometries.map(geometry =>
      this.scaleDownObject(new Mesh(geometry, this.getStandardMaterial())) as Mesh,
    )
  }
}

export default STLFileLoader
